import { type FC, useState } from 'react'
import { AlertCircle, RefreshCw, RotateCcw, XCircle } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    useCancelQueuedTransaction,
    useQueuedTransactions,
    useRestartQueuedTransaction,
} from '@/hooks/useLiquidityQueue'
import type { QueuedTransaction } from '@/types/liquidity-queue'

const PAGE_SIZE = 20

const describe = (item: QueuedTransaction) =>
    item.fromCurrency &&
    item.toCurrency &&
    item.fromCurrency !== item.toCurrency
        ? `${item.fromCurrency} → ${item.toCurrency}`
        : item.currency

const errorMessage = (error: unknown) =>
    (error as { response?: { data?: { message?: string } } })?.response?.data
        ?.message ||
    (error instanceof Error ? error.message : 'Unexpected error')

/**
 * Transactions parked because company liquidity was short when the user
 * confirmed them. The user's funds stay held until an admin restarts the
 * transaction (once liquidity covers it) or cancels it and refunds the user.
 */
export const QueuedTransactionsTab: FC = () => {
    const [page, setPage] = useState(1)
    const [toCancel, setToCancel] = useState<QueuedTransaction | null>(null)

    const { data, isLoading, error, refetch, isFetching } =
        useQueuedTransactions(page, PAGE_SIZE)
    const restart = useRestartQueuedTransaction()
    const cancel = useCancelQueuedTransaction()

    const handleRestart = (item: QueuedTransaction) => {
        restart.mutate(item.id, {
            onSuccess: res =>
                res.data?.activated
                    ? toast.success(res.message)
                    : toast.warning(
                          res.data?.reason === 'insufficient_liquidity'
                              ? 'Still not enough company liquidity to run this transaction'
                              : res.message
                      ),
            onError: err =>
                toast.error('Could not restart transaction', {
                    description: errorMessage(err),
                }),
        })
    }

    const handleCancel = () => {
        if (!toCancel) return
        cancel.mutate(toCancel.id, {
            onSuccess: res => {
                toast.success(res.message)
                setToCancel(null)
            },
            onError: err =>
                toast.error('Could not cancel transaction', {
                    description: errorMessage(err),
                }),
        })
    }

    if (isLoading) {
        return (
            <div className="rounded-md border">
                <Skeleton className="h-12 w-full rounded-t-md" />
                {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-14 w-full" />
                ))}
            </div>
        )
    }

    if (error) {
        return (
            <div className="flex h-[300px] flex-col items-center justify-center gap-4 text-center">
                <div className="rounded-full bg-red-100 p-3">
                    <AlertCircle className="h-6 w-6 text-red-600" />
                </div>
                <p className="text-sm text-muted-foreground">
                    {errorMessage(error)}
                </p>
                <Button
                    variant="outline"
                    className="gap-2"
                    onClick={() => refetch()}
                >
                    <RefreshCw className="h-4 w-4" />
                    Retry
                </Button>
            </div>
        )
    }

    const items = Array.isArray(data?.data) ? data.data : []
    const pagination = data?.pagination

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between gap-4">
                <p className="text-sm text-muted-foreground">
                    Transactions waiting for company liquidity. The user&apos;s
                    funds are held until each one is restarted or cancelled;
                    anything left for 3 days is failed and refunded
                    automatically.
                </p>
                <Button
                    variant="outline"
                    size="sm"
                    className="gap-2"
                    onClick={() => refetch()}
                    disabled={isFetching}
                >
                    <RefreshCw
                        className={
                            isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'
                        }
                    />
                    Refresh
                </Button>
            </div>

            <div className="rounded-md border">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Queued at</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Currency</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>User ID</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead className="text-right">
                                Actions
                            </TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {items.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={7}
                                    className="h-24 text-center text-muted-foreground"
                                >
                                    No transactions are waiting for liquidity.
                                </TableCell>
                            </TableRow>
                        ) : (
                            items.map(item => {
                                const busy =
                                    (restart.isPending &&
                                        restart.variables === item.id) ||
                                    (cancel.isPending &&
                                        cancel.variables === item.id)
                                return (
                                    <TableRow key={item.id}>
                                        <TableCell className="whitespace-nowrap">
                                            {new Date(
                                                item.createdAt
                                            ).toLocaleString()}
                                        </TableCell>
                                        <TableCell>
                                            {item.context ?? '—'}
                                        </TableCell>
                                        <TableCell>{describe(item)}</TableCell>
                                        <TableCell className="whitespace-nowrap">
                                            {item.amount} {item.currency}
                                        </TableCell>
                                        <TableCell className="font-mono text-xs">
                                            {item.userId ?? '—'}
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                variant="secondary"
                                                className="bg-yellow-100 text-yellow-700 hover:bg-yellow-100"
                                            >
                                                Awaiting liquidity
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    className="gap-1"
                                                    disabled={busy}
                                                    onClick={() =>
                                                        handleRestart(item)
                                                    }
                                                >
                                                    <RotateCcw className="h-4 w-4" />
                                                    Restart
                                                </Button>
                                                <Button
                                                    variant="destructive"
                                                    size="sm"
                                                    className="gap-1"
                                                    disabled={busy}
                                                    onClick={() =>
                                                        setToCancel(item)
                                                    }
                                                >
                                                    <XCircle className="h-4 w-4" />
                                                    Cancel &amp; refund
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )
                            })
                        )}
                    </TableBody>
                </Table>
            </div>

            {pagination && pagination.totalPages > 1 && (
                <div className="flex items-center justify-end gap-2">
                    <span className="text-sm text-muted-foreground">
                        Page {pagination.page} of {pagination.totalPages}
                    </span>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={!pagination.hasPrev}
                        onClick={() => setPage(p => p - 1)}
                    >
                        Previous
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={!pagination.hasNext}
                        onClick={() => setPage(p => p + 1)}
                    >
                        Next
                    </Button>
                </div>
            )}

            <Dialog
                open={!!toCancel}
                onOpenChange={open =>
                    !open && !cancel.isPending && setToCancel(null)
                }
            >
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            Cancel and refund this transaction?
                        </DialogTitle>
                        <DialogDescription>
                            {toCancel && (
                                <>
                                    The{' '}
                                    {toCancel.context?.toLowerCase() ??
                                        'transaction'}{' '}
                                    of {toCancel.amount} {describe(toCancel)}{' '}
                                    will be marked <strong>Cancelled</strong>{' '}
                                    and the funds held for it, including its
                                    platform fee, go back to the user. It was
                                    never sent to Quidax, so nothing else moves.
                                    This cannot be undone.
                                </>
                            )}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            disabled={cancel.isPending}
                            onClick={() => setToCancel(null)}
                        >
                            Keep it queued
                        </Button>
                        <Button
                            variant="destructive"
                            disabled={cancel.isPending}
                            onClick={handleCancel}
                        >
                            {cancel.isPending
                                ? 'Cancelling…'
                                : 'Cancel & refund'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    )
}
